import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { ListResponse } from "../../api/types";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { PanelSkeleton } from "../../ui/skeletons";
import type { WebhookDelivery, WebhookEndpoint } from "./types";

// Webhooks are how another system finds out what happened here. The delivery history sits beside
// the endpoints on purpose: an endpoint that looks configured and never delivers is the failure
// worth catching, and it is invisible if the two are on separate screens.
export function WebhooksPanel() {
  const webhooks = useQuery({
    queryKey: ["messaging", "webhooks"],
    queryFn: () => api<ListResponse<WebhookEndpoint>>("/api/v1/messaging/webhook-endpoints"),
  });
  const webhookDeliveries = useQuery({
    queryKey: ["messaging", "webhook-deliveries"],
    queryFn: () =>
      api<ListResponse<WebhookDelivery>>("/api/v1/messaging/webhook-deliveries?limit=25"),
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [secret, setSecret] = useState("");

  async function createWebhook(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    setSecret("");
    try {
      const item = await api<WebhookEndpoint>("/api/v1/messaging/webhook-endpoints", {
        method: "POST",
        body: jsonBody({
          name: String(form.get("name")),
          url: String(form.get("url")),
          eventTypes: String(form.get("eventTypes"))
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean),
          active: true,
          version: 0,
        }),
      });
      setSecret(item.secret ?? "");
      formElement.reset();
      await webhooks.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function webhookAction(item: WebhookEndpoint, action: "verify" | "rotate" | "toggle") {
    setWorking(true);
    setError(undefined);
    try {
      if (action === "toggle")
        await api(`/api/v1/messaging/webhook-endpoints/${item.id}`, {
          method: "PUT",
          body: jsonBody({
            name: item.name,
            url: item.url,
            eventTypes: item.eventTypes,
            active: !item.active,
            version: item.version,
          }),
        });
      else {
        const updated = await api<WebhookEndpoint>(
          `/api/v1/messaging/webhook-endpoints/${item.id}/${action === "rotate" ? "rotate-secret" : "verify"}`,
          { method: "POST", body: jsonBody({}) },
        );
        if (updated.secret) setSecret(updated.secret);
      }
      await Promise.all([webhooks.refetch(), webhookDeliveries.refetch()]);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (webhooks.isLoading) return <PanelSkeleton />;
  const queryError = webhooks.error ?? webhookDeliveries.error;
  return (
    <div className="stack">
      {error || queryError ? <ErrorPanel error={error ?? queryError} /> : null}
      <div className="content-grid">
        <Card className="span-6">
          <CardHeader
            title="Webhooks"
            description="Send automatic updates to another system. Ask your IT provider for the URL and signing details."
          />
          <form className="form-stack" onSubmit={createWebhook}>
            <Field label="Name">
              <input name="name" required />
            </Field>
            <Field label="HTTPS URL">
              <input
                name="url"
                type="url"
                required
                placeholder="https://hooks.example.gov/venueops"
              />
            </Field>
            <Field label="Event types" hint="Comma-separated event names, or * for every event.">
              <input name="eventTypes" defaultValue="booking.confirmed,invoice.paid" required />
            </Field>
            <FormActions submitting={working} submitLabel="Create webhook" />
          </form>
          {secret ? (
            <Notice tone="warning" title="Copy this signing secret now">
              <strong className="mono">{secret}</strong>
            </Notice>
          ) : null}
          {webhooks.data?.items.length ? (
            <DataTable
              caption="Webhook endpoints"
              items={webhooks.data.items}
              getKey={(item) => item.id}
              columns={[
                {
                  label: "Endpoint",
                  render: (item) => (
                    <>
                      <strong>{item.name}</strong>
                      <br />
                      <small>{item.url}</small>
                    </>
                  ),
                },
                {
                  label: "Verification",
                  render: (item) => <StatusPill value={item.verificationState} />,
                },
                {
                  label: "Action",
                  render: (item) => (
                    <div className="inline">
                      <button
                        className="button secondary small"
                        type="button"
                        disabled={working}
                        onClick={() => webhookAction(item, "verify")}
                      >
                        Verify
                      </button>
                      <button
                        className="button secondary small"
                        type="button"
                        disabled={working}
                        onClick={() => webhookAction(item, "rotate")}
                      >
                        Rotate secret
                      </button>
                      <button
                        className="button secondary small"
                        type="button"
                        disabled={working}
                        onClick={() => webhookAction(item, "toggle")}
                      >
                        {item.active ? "Pause" : "Enable"}
                      </button>
                    </div>
                  ),
                },
              ]}
            />
          ) : (
            <EmptyState
              title="No webhooks"
              description="Create an endpoint when another system needs event notifications."
            />
          )}
        </Card>
        <Card className="span-6">
          <CardHeader title="Recent webhook deliveries" />
          {webhookDeliveries.data?.items.length ? (
            <DataTable
              caption="Recent webhook deliveries"
              items={webhookDeliveries.data.items}
              getKey={(item) => item.id}
              columns={[
                {
                  label: "Endpoint",
                  render: (item) => <span className="mono">{item.endpointId.slice(0, 8)}</span>,
                },
                { label: "Status", render: (item) => <StatusPill value={item.status} /> },
                { label: "HTTP", render: (item) => item.responseStatus ?? "Not available" },
                { label: "Attempts", render: (item) => item.attempts },
              ]}
            />
          ) : (
            <EmptyState
              title="No webhook deliveries"
              description="Attempts and response status will appear here."
            />
          )}
        </Card>
      </div>
    </div>
  );
}
